import React, { useCallback, useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import { CalendarPlus, ExternalLink, ListTodo, Loader2, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import {
  buildAiDraftFormRoute,
  draftFromEmail,
  openAiDraftForm,
  saveAiDraftDirectly,
  useAiAssistantEnabled,
  type AiDraftEntityType,
} from '../../ai/useAiAssistant';
import { CONVERSATIONS_KEY } from '../../ai-chat/useAiChat';
import type { AiDraftFromEmailResult } from '../../ai/types';

export interface UseEmailAiDraftOptions {
  emailId: number;
  partitionAt?: string | null;
  onDraftResult?: (result: AiDraftFromEmailResult) => void;
  onSaved?: (result: { entityType: AiDraftEntityType; entityId: number }) => void;
}

export function useEmailAiDraft({
  emailId,
  partitionAt,
  onDraftResult,
  onSaved,
}: UseEmailAiDraftOptions) {
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  const { showError, showSuccess } = useNotification();
  const enabled = useAiAssistantEnabled();
  const [loading, setLoading] = useState<'todo' | 'appointment' | null>(null);
  const [saving, setSaving] = useState(false);
  const [draftPreview, setDraftPreview] = useState<AiDraftFromEmailResult | null>(null);

  useEffect(() => {
    setDraftPreview(null);
    setLoading(null);
  }, [emailId, partitionAt]);

  const handleDraft = useCallback(
    async (draftType: 'todo' | 'appointment') => {
      setLoading(draftType);
      try {
        const result = await draftFromEmail({ emailId, partitionAt, draftType });
        const usable =
          (draftType === 'todo' && result.todo?.todoDescr) ||
          (draftType === 'appointment' && result.appointment?.clientAppName);
        if (!usable) {
          showError('AI did not return a usable draft.');
          return;
        }
        if (onDraftResult) {
          onDraftResult(result);
        } else {
          setDraftPreview(result);
          showSuccess('AI draft ready — review below.');
        }
        void globalMutate(CONVERSATIONS_KEY);
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        showError(message || 'Could not draft from email');
      } finally {
        setLoading(null);
      }
    },
    [emailId, partitionAt, onDraftResult, showError, showSuccess, globalMutate],
  );

  const openDraftForm = useCallback(() => {
    if (!draftPreview || !openAiDraftForm(navigate, draftPreview)) {
      showError('Could not open form — draft data is incomplete.');
    }
  }, [draftPreview, navigate, showError]);

  const dismissPreview = useCallback(() => setDraftPreview(null), []);

  const saveDraftDirectly = useCallback(async () => {
    if (!draftPreview) return;
    setSaving(true);
    try {
      const saved = await saveAiDraftDirectly({
        draft: draftPreview,
        emailId,
        partitionAt,
      });
      showSuccess(
        saved.entityType === 'todo'
          ? 'Todo saved and linked to this email.'
          : 'Appointment saved and linked to this email.',
      );
      setDraftPreview(null);
      onSaved?.(saved);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : err instanceof Error
            ? err.message
            : undefined;
      showError(message || 'Could not save draft');
    } finally {
      setSaving(false);
    }
  }, [draftPreview, emailId, partitionAt, onSaved, showError, showSuccess]);

  return {
    enabled,
    loading,
    saving,
    draftPreview,
    handleDraftTodo: () => void handleDraft('todo'),
    handleDraftAppointment: () => void handleDraft('appointment'),
    openDraftForm,
    saveDraftDirectly: () => void saveDraftDirectly(),
    dismissPreview,
  };
}

interface EmailAiCrmActionsProps {
  emailId: number;
  partitionAt?: string | null;
  /** Inline buttons for embedding in a toolbar row. */
  inline?: boolean;
  /** Show draft preview below buttons (default on detail view). */
  showDraftPreview?: boolean;
  onDraftResult?: (result: AiDraftFromEmailResult) => void;
}

const EmailAiCrmActions: React.FC<EmailAiCrmActionsProps> = ({
  emailId,
  partitionAt,
  inline = false,
  showDraftPreview = false,
  onDraftResult,
}) => {
  const {
    enabled,
    loading,
    draftPreview,
    handleDraftTodo,
    handleDraftAppointment,
    openDraftForm,
    saveDraftDirectly,
    saving,
    dismissPreview,
  } = useEmailAiDraft({ emailId, partitionAt, onDraftResult });

  if (!enabled) return null;

  return (
    <div className={cn(!inline && 'space-y-2')}>
      <div className={cn('flex flex-wrap items-center gap-2', inline && 'contents')}>
        <Button
          variant="outline"
          size="sm"
          disabled={loading !== null}
          onClick={handleDraftTodo}
          className="gap-1.5"
        >
          <ListTodo className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {loading === 'todo' ? 'Drafting…' : 'Todo'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={loading !== null}
          onClick={handleDraftAppointment}
          className="gap-1.5"
        >
          <CalendarPlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {loading === 'appointment' ? 'Drafting…' : 'Appointment'}
        </Button>
      </div>
      {showDraftPreview && draftPreview ? (
        <AiDraftPreview
          draft={draftPreview}
          onOpenForm={openDraftForm}
          onSaveDirectly={() => void saveDraftDirectly()}
          saving={saving}
          onDismiss={dismissPreview}
        />
      ) : null}
    </div>
  );
};

interface AiDraftPreviewProps {
  draft: AiDraftFromEmailResult;
  onOpenForm: () => void;
  onSaveDirectly: () => void;
  saving?: boolean;
  onDismiss: () => void;
}

export const AiDraftPreview: React.FC<AiDraftPreviewProps> = ({
  draft,
  onOpenForm,
  onSaveDirectly,
  saving = false,
  onDismiss,
}) => {
  const { colors } = useTheme();
  const isTodo = Boolean(draft.todo?.todoDescr) && !draft.appointment?.clientAppName;
  const formHref = buildAiDraftFormRoute(draft);
  const saveAccent = isTodo
    ? 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:hover:bg-emerald-900/60 dark:text-emerald-300'
    : 'text-sky-700 bg-sky-100 hover:bg-sky-200 dark:bg-sky-900/40 dark:hover:bg-sky-900/60 dark:text-sky-300';

  return (
    <div
      className={cn(
        'rounded-xl border p-3 sm:p-4',
        isTodo ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-sky-500/30 bg-sky-500/5',
        colors.border,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {isTodo ? (
            <ListTodo className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
          ) : (
            <CalendarPlus className="h-4 w-4 text-sky-600 dark:text-sky-400" aria-hidden />
          )}
          <p className={cn('text-sm font-semibold', colors.text)}>
            AI draft — {isTodo ? 'Todo' : 'Appointment'}
          </p>
        </div>
        <button
          type="button"
          className={cn('text-xs hover:underline', colors.textMuted)}
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>

      {isTodo && draft.todo ? (
        <div className={cn('text-sm space-y-1', colors.textSecondary)}>
          <p className={cn('font-medium', colors.text)}>{draft.todo.todoDescr}</p>
          {draft.todo.todoDueDate ? (
            <p className="text-xs">Due: {new Date(draft.todo.todoDueDate).toLocaleString()}</p>
          ) : null}
        </div>
      ) : null}

      {!isTodo && draft.appointment ? (
        <div className={cn('text-sm space-y-1', colors.textSecondary)}>
          <p className={cn('font-medium', colors.text)}>{draft.appointment.clientAppName}</p>
          {draft.appointment.clientAppScheduleDateTime ? (
            <p className="text-xs">
              When: {new Date(draft.appointment.clientAppScheduleDateTime).toLocaleString()}
            </p>
          ) : null}
        </div>
      ) : null}

      {draft.notes ? (
        <p className={cn('text-xs mt-2 italic', colors.textMuted)}>{draft.notes}</p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onSaveDirectly}
          disabled={saving}
          className={cn(
            'h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            saveAccent,
          )}
          title={isTodo ? 'Save todo directly' : 'Save appointment directly'}
          aria-label={isTodo ? 'Save todo directly' : 'Save appointment directly'}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Save className="h-4 w-4" aria-hidden />
          )}
        </button>
        <Button
          variant="outline"
          size="sm"
          onClick={onOpenForm}
          disabled={!formHref || saving}
          className="gap-1.5"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          Open {isTodo ? 'todo' : 'appointment'} form
        </Button>
      </div>
    </div>
  );
};

// Re-export helper for parent to open form from preview
export function useOpenAiDraft(navigate: ReturnType<typeof useNavigate>) {
  return useCallback(
    (result: AiDraftFromEmailResult) => {
      openAiDraftForm(navigate, result);
    },
    [navigate],
  );
}

export default EmailAiCrmActions;
